export interface PopoverPosition {
  left: number;
  /** Ancrage haut (ouverture vers le bas) */
  top?: number;
  /** Ancrage bas (ouverture vers le haut) : la hauteur peut varier sans décoller du déclencheur */
  bottom?: number;
}

const GAP = 4;
const MARGIN = 8;

/**
 * Position d'un popover en `position: fixed` (pour passer au-dessus des panneaux qui coupent leur contenu) :
 * sous le déclencheur, ou au-dessus quand la place manque en bas ; jamais hors de l'écran.
 * Trop haut pour un côté comme pour l'autre : il se cale en bas de l'écran (en haut s'il dépasse
 * l'écran, où sa hauteur est alors bornée), quitte à recouvrir le déclencheur.
 */
export function computePopoverPosition(
  trigger: { top: number; bottom: number; left: number },
  popover: { width: number; height: number },
  viewport: { width: number; height: number },
): PopoverPosition {
  const left = Math.max(MARGIN, Math.min(trigger.left, viewport.width - popover.width - MARGIN));
  const spaceBelow = viewport.height - trigger.bottom - GAP - MARGIN;
  const spaceAbove = trigger.top - GAP - MARGIN;
  if (popover.height <= spaceBelow) return { left, top: trigger.bottom + GAP };
  if (popover.height <= spaceAbove) return { left, bottom: viewport.height - trigger.top + GAP };
  return { left, top: Math.max(MARGIN, viewport.height - popover.height - MARGIN) };
}

/** Applique la position calculée au popover et le rend visible (il est masqué tant qu'il n'est pas placé) */
export function placeFixedPopover(trigger: HTMLElement, popover: HTMLElement): void {
  // Plus haut que l'écran : hauteur bornée, le contenu défile
  popover.style.maxHeight = `${window.innerHeight - 2 * MARGIN}px`;
  popover.style.overflowY = "auto";
  const position = computePopoverPosition(
    trigger.getBoundingClientRect(),
    { width: popover.offsetWidth, height: popover.offsetHeight },
    { width: window.innerWidth, height: window.innerHeight },
  );
  // Sous un ancêtre transformé (galet), `fixed` part de cet ancêtre et non de l'écran : on mesure son décalage
  const origin = fixedOrigin(popover);
  popover.style.left = `${position.left - origin.left}px`;
  popover.style.top = position.top === undefined ? "auto" : `${position.top - origin.top}px`;
  popover.style.bottom = position.bottom === undefined ? "auto" : `${position.bottom - origin.bottom}px`;
  popover.style.visibility = "visible";
}

/** Décalage, par rapport à l'écran, du repère de `position: fixed` vu depuis le parent du popover */
function fixedOrigin(popover: HTMLElement): { left: number; top: number; bottom: number } {
  const probe = document.createElement("div");
  probe.style.cssText = "position:fixed;top:0;left:0;bottom:0;width:0;visibility:hidden;pointer-events:none";
  popover.parentElement!.appendChild(probe);
  const rect = probe.getBoundingClientRect();
  probe.remove();
  return { left: rect.left, top: rect.top, bottom: window.innerHeight - rect.bottom };
}
